import { Wishlist, WishlistEntry } from '@models/IWishlistCardSwitch';
import removeExtensionFromPath from '@utility/helpers/removeExtensionFromPath';
import {
  addItem,
  deleteItemByEntityId,
  getItem,
  removeItem,
  updateItem,
} from '@utility/hooks/useIndexDb';
import { create } from 'zustand';

interface LikedItemsState {
  likedItems: WishlistEntry[];
  isLoading: boolean;
  fetchLikedItems: () => Promise<void>;
  addLikedItem: (item: WishlistEntry) => Promise<IDBValidKey>;
  updateLikedItem: (item: WishlistEntry) => Promise<void>;
  removeLikedItem: (id: string, wishlistId?: number) => Promise<void>;
}

interface WishlistState {
  wishlists: Wishlist[];
  isLoading: boolean;
  fetchWishlists: () => Promise<void>;
  addWishlist: (
    item: Omit<Wishlist, 'id'> & { id?: number },
  ) => Promise<IDBValidKey>;
  updateWishlist: (item: Wishlist) => Promise<void>;
  deleteWishlist: (id: number) => Promise<void>;
}

export const useLikedItemsStore = create<LikedItemsState>((set) => ({
  likedItems: [],
  isLoading: true,
  fetchLikedItems: async () => {
    set({ isLoading: true });
    try {
      const likedItems = await getItem('jotun-deco', 'likes');
      set({ likedItems });
    } catch (error) {
      console.error('Error fetching liked items from IndexedDB:', error);
    } finally {
      set({ isLoading: false });
    }
  },
  addLikedItem: async (item: WishlistEntry) => {
    set({ isLoading: true });

    try {
      const likeItemId = await addItem(item, 'jotun-deco', 'likes');
      const likedItems = await getItem('jotun-deco', 'likes');
      set({ likedItems });
      return likeItemId;
    } catch (error) {
      console.error('Error adding liked item to IndexedDB:', error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  updateLikedItem: async (item: WishlistEntry) => {
    set({ isLoading: true });

    try {
      await updateItem(item, 'jotun-deco', 'likes');
      const likedItems = await getItem('jotun-deco', 'likes');
      set({ likedItems });
    } catch (error) {
      console.error('Error updating liked item to IndexedDB:', error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  removeLikedItem: async (id: string, wishlistId?: number) => {
    set({ isLoading: true });
    const formattedId = removeExtensionFromPath(id);

    try {
      const wishlists = await getItem('jotun-deco', 'lists');
      const allLikedItems = await getItem('jotun-deco', 'likes');

      const likedItem = allLikedItems.find((item: WishlistEntry) => {
        const itemId = removeExtensionFromPath(item.entity.id);

        const likedItemLink =
          item.entity.data?.linkUrl || item.entity.fields?.linkUrl;

        const formattedLikedItemLink = likedItemLink
          ? removeExtensionFromPath(likedItemLink)
          : null;

        return itemId === formattedId || formattedLikedItemLink === formattedId;
      });

      const singleList = wishlistId
        ? wishlists.find((wl) => wl.id === wishlistId)
        : null;

      // Remove from from all wishlists
      if (!singleList) {
        await deleteItemByEntityId(formattedId, 'jotun-deco', 'likes');

        wishlists.forEach(async (wishlist) => {
          if (wishlist.likes.includes(likedItem.id)) {
            wishlist.likes = wishlist.likes.filter(
              (likeId: number) => likeId !== likedItem.id,
            );
            await updateItem(wishlist, 'jotun-deco', 'lists');
          }
        });

        // Remove from single wishlist
      } else {
        // Check if the like is present in any other wishlist
        const isLikeInOtherLists = wishlists.some(
          (wishlist) =>
            wishlist.id !== singleList.id &&
            wishlist.likes.includes(likedItem.id),
        );

        // If the like is not in any other list, delete it
        if (!isLikeInOtherLists) {
          await deleteItemByEntityId(formattedId, 'jotun-deco', 'likes');
        }

        // Remove the like ID from the wishlist's likes array
        singleList.likes = singleList.likes.filter(
          (likeId: number) => likeId !== likedItem.id,
        );

        // Update the wishlist in IndexedDB
        await updateItem(singleList, 'jotun-deco', 'lists');

        // Fetch and set updated liked items
        const likedItems = await getItem('jotun-deco', 'likes');
        set({ likedItems });
      }
    } catch (error) {
      console.error(
        'Error removing liked item from wishlist in IndexedDB:',
        error,
      );
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
}));

export const useWishlistStore = create<WishlistState>((set) => ({
  wishlists: [],
  isLoading: true,
  fetchWishlists: async () => {
    set({ isLoading: true });
    try {
      const wishlists = await getItem('jotun-deco', 'lists');
      set({ wishlists });
    } catch (error) {
      console.error('Error fetching wishlists from IndexedDB:', error);
    } finally {
      set({ isLoading: false });
    }
  },
  addWishlist: async (item: Omit<Wishlist, 'id'> & { id?: number }) => {
    set({ isLoading: true });
    try {
      const wishlistId = await addItem(item, 'jotun-deco', 'lists');
      const wishlists = await getItem('jotun-deco', 'lists');
      set({ wishlists });
      return wishlistId;
    } catch (error) {
      console.error('Error adding wishlist to IndexedDB:', error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  updateWishlist: async (item: Wishlist) => {
    set({ isLoading: true });
    try {
      await updateItem(item, 'jotun-deco', 'lists');
      const wishlists = await getItem('jotun-deco', 'lists');
      set({ wishlists });
    } catch (error) {
      console.error('Error updating wishlist in IndexedDB:', error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  deleteWishlist: async (id: number) => {
    set({ isLoading: true });
    try {
      // Retrieve the wishlist to delete
      const wishlists = await getItem('jotun-deco', 'lists');
      const wishlistToDelete = wishlists.find((wishlist) => wishlist.id === id);

      if (wishlistToDelete) {
        // Remove all associated likes
        const likedItemsStore = useLikedItemsStore.getState();

        for (const likeId of wishlistToDelete.likes) {
          const likeItem = likedItemsStore.likedItems.find(
            (item) => item.id === likeId,
          );

          if (likeItem) {
            await likedItemsStore.removeLikedItem(likeItem.entity.id, id);
          }
        }

        // Delete the wishlist
        await removeItem(id, 'jotun-deco', 'lists');

        // Update the wishlists state
        const updatedWishlists = await getItem('jotun-deco', 'lists');
        set({ wishlists: updatedWishlists });
      }
    } catch (error) {
      console.error('Error deleting wishlist from IndexedDB:', error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
}));
